import type { Buff, Card, Bomb, OptimizerConfig } from './engine';

export type LibraryCard = Card & { enabled: boolean };
export type LibraryBomb = Bomb & { enabled: boolean };
export interface CardLibrary { version: 1; cards: LibraryCard[]; bombs: LibraryBomb[] }
export const LIBRARY_KEY = 'blackstar-card-library-v1';
export const BUFF_TYPES = ['cast', 'score', 'comb', 'judge', 'star', 'event_bonus'] as const;
export const TEAMS = ['K', 'W', 'P', 'B', 'C'] as const;
export const cloneLibrary = (library: CardLibrary): CardLibrary => JSON.parse(JSON.stringify(library));
export const defaultLibrary = (config: OptimizerConfig): CardLibrary => ({
    version: 1, cards: config.cards.map(card => ({ ...card, enabled: true })),
    bombs: config.bombs.map(bomb => ({ ...bomb, enabled: true })),
});

/** Validate persisted/user-edited data before it reaches the search worker. */
export function validateLibrary(value: unknown): asserts value is CardLibrary {
    const library = value as CardLibrary;
    if (!library || library.version !== 1 || !Array.isArray(library.cards) || !Array.isArray(library.bombs)) throw new Error('Invalid card library format');
    const names = new Set<string>();
    const finite = (n: number) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
    const identity = (card: LibraryCard | LibraryBomb) => {
        if (!card || typeof card.name !== 'string' || !card.name.trim() || typeof card.chara !== 'string' || !card.chara.trim()) throw new Error('Every card needs a name and character');
        if (names.has(card.name.trim())) throw new Error(`Card names must be unique: ${card.name}`);
        names.add(card.name.trim());
        if (!TEAMS.includes(card.team as typeof TEAMS[number])) throw new Error(`${card.name}: choose a team`);
        if (typeof card.enabled !== 'boolean') throw new Error(`${card.name}: invalid enabled setting`);
    };
    const buffs = (list: Buff[], name: string, passive = false) => {
        if (!Array.isArray(list)) throw new Error(`${name}: invalid buffs`);
        for (const buff of list) {
            if (!buff || !BUFF_TYPES.includes(buff.buff_type as typeof BUFF_TYPES[number]) || (passive && buff.buff_type !== 'event_bonus')) throw new Error(`${name}: unsupported buff type`);
            if (!Array.isArray(buff.rate) || buff.rate.length !== 5 || !buff.rate.every(finite)) throw new Error(`${name}: each buff needs five nonnegative lane values`);
            if (buff.buff_type === 'judge' && buff.rate.some(n => ![0, .4, .6].includes(n))) throw new Error(`${name}: judge buffs must be 0%, 40% or 60%`);
            if (buff.buff_type === 'star' && (!Number.isInteger(buff.rate[0]) || !buff.rate.every(n => n === buff.rate[0]))) throw new Error(`${name}: stars must be a whole number shared by all lanes`);
        }
    };
    library.cards.forEach(card => {
        identity(card);
        if (!Array.isArray(card.copies) || !card.copies.length) throw new Error(`${card.name}: add at least one skill use`);
        card.copies.forEach(copy => {
            if (!copy || (copy.effective_time !== null && (!finite(copy.effective_time) || copy.effective_time === 0))) throw new Error(`${card.name}: duration must be positive, or untimed`);
            buffs(copy.buffs, card.name);
        });
        buffs(card.passive_buffs, card.name, true);
    });
    library.bombs.forEach(bomb => {
        identity(bomb);
        if (!Number.isInteger(bomb.measure) || bomb.measure < 1 || !finite(bomb.rate)) throw new Error(`${bomb.name}: duration must be a positive whole number and bomb rate must be nonnegative`);
        if (!Array.isArray(bomb.lanes) || !bomb.lanes.length || new Set(bomb.lanes).size !== bomb.lanes.length || bomb.lanes.some(n => !Number.isInteger(n) || n < 0 || n > 4)) throw new Error(`${bomb.name}: select at least one lane`);
        if (typeof bomb.once_only !== 'boolean') throw new Error(`${bomb.name}: invalid once-only setting`);
    });
    if (!library.cards.some(card => card.enabled) || !library.bombs.some(bomb => bomb.enabled)) throw new Error('Include at least one setup card and one bomb card in the search');
}

export function readSavedLibrary(): CardLibrary | null {
    try {
        const saved = localStorage.getItem(LIBRARY_KEY);
        if (!saved) return null;
        const value: unknown = JSON.parse(saved);
        validateLibrary(value);
        return value;
    } catch { return null; }
}

export function saveLibrary(library: CardLibrary) {
    validateLibrary(library);
    // Let storage failures reach the editor; do not claim unsaved changes were saved.
    localStorage.setItem(LIBRARY_KEY, JSON.stringify(library));
}

export async function loadDefaultLibrary(): Promise<CardLibrary> {
    const response = await fetch(`${process.env.PUBLIC_URL || '.'}/optimizer/config.json`);
    if (!response.ok) throw new Error(`Unable to load default cards (HTTP ${response.status})`);
    const library = defaultLibrary(await response.json());
    validateLibrary(library);
    return library;
}

export function applyLibrary(config: OptimizerConfig, library?: CardLibrary): OptimizerConfig {
    if (!library) return config;
    validateLibrary(library);
    return { ...config, cards: library.cards.filter(card => card.enabled), bombs: library.bombs.filter(bomb => bomb.enabled) };
}
